import { useEffect } from "react";
import { useMatches, useRouter } from "@tanstack/react-router";

export function DocumentMeta() {
  const matches = useMatches();
  const router = useRouter();

  useEffect(() => {
    const DEFAULT_TITLE = "LetsListBusiness — Discover Local Businesses & Services";
    const DEFAULT_DESC =
      "Find trusted local businesses, restaurants, coffee shops, home services and more across major U.S. cities. Real reviews, real recommendations.";

    let title = DEFAULT_TITLE;
    const metaMap: Map<string, { el: HTMLMetaElement; content: string }> = new Map();
    const linkMap: Map<string, { el?: HTMLLinkElement; href: string }> = new Map();
    const schemaTags: object[] = [];

    // Walk each matched route and call its head() function
    for (const match of matches) {
      const route = (router as any).routesById?.[match.routeId];
      const headFn = route?.options?.head;
      if (typeof headFn !== "function") continue;

      let result: any;
      try {
        result = headFn({ loaderData: match.loaderData, params: match.params });
      } catch {
        continue;
      }
      if (!result) continue;

      // Meta tags
      if (Array.isArray(result.meta)) {
        for (const tag of result.meta) {
          if (tag.title) {
            title = tag.title;
          } else if (tag.name && tag.content !== undefined) {
            metaMap.set(`name:${tag.name}`, { el: undefined as any, content: tag.content });
          } else if (tag.property && tag.content !== undefined) {
            metaMap.set(`property:${tag.property}`, { el: undefined as any, content: tag.content });
          }
        }
      }

      // Link tags (canonical etc.)
      if (Array.isArray(result.links)) {
        for (const tag of result.links) {
          if (tag.rel && tag.href) {
            linkMap.set(tag.rel, { href: tag.href });
          }
        }
      }

      // Schema / JSON-LD
      const schemas: any[] = Array.isArray(result.schema)
        ? result.schema
        : result.schema
          ? [result.schema]
          : [];
      schemaTags.push(...schemas);

      if (Array.isArray(result.scripts)) {
        for (const s of result.scripts) {
          if (s.type === "application/ld+json" && s.children) {
            try { schemaTags.push(JSON.parse(s.children)); } catch { /* ignore */ }
          }
        }
      }
    }

    // ── Apply title ────────────────────────────────────────────────────────────
    document.title = title;

    // ── Update description meta ────────────────────────────────────────────────
    const descEntry = metaMap.get("name:description");
    let descEl = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!descEl) {
      descEl = document.createElement("meta");
      descEl.name = "description";
      document.head.appendChild(descEl);
    }
    descEl.content = descEntry ? descEntry.content : DEFAULT_DESC;

    // ── Remove previously injected tags ───────────────────────────────────────
    document.querySelectorAll("[data-dm-injected]").forEach((el) => el.remove());

    // ── Inject remaining meta tags ─────────────────────────────────────────────
    for (const [key, { content }] of metaMap) {
      if (key === "name:description") continue; // already handled above

      const el = document.createElement("meta");
      el.setAttribute("data-dm-injected", "true");

      if (key.startsWith("name:")) {
        el.name = key.slice(5);
        el.content = content;
      } else if (key.startsWith("property:")) {
        el.setAttribute("property", key.slice(9));
        el.content = content;
      }
      document.head.appendChild(el);
    }

    // ── Inject / update link tags (canonical) ─────────────────────────────────
    for (const [rel, { href }] of linkMap) {
      let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.rel = rel;
        el.setAttribute("data-dm-injected", "true");
        document.head.appendChild(el);
      }
      el.href = href;
    }

    // ── Inject JSON-LD scripts ─────────────────────────────────────────────────
    // Remove old ones
    document.querySelectorAll('script[type="application/ld+json"][data-dm-injected]').forEach((el) => el.remove());
    for (let i = 0; i < schemaTags.length; i++) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = JSON.stringify(schemaTags[i]);
      script.setAttribute("data-dm-injected", "true");
      document.head.appendChild(script);
    }
  }, [matches, router]);

  return null;
}
